'use client';

import { addRx } from 'actions/Database';
import { DBRx } from 'db/schema/drugs';
import { LegacyRef, useCallback, useEffect, useState } from 'react';
import styles from 'styles/sunfire.forms.module.css';
import { Packaging, Rx } from 'types/SunFire';
import AddCircleIcon from 'components/icons/AddCircleIcon';

interface DrugModalProps {
  drug: Rx | undefined;
  packaging: Packaging[];
  addPrescription: (drug: DBRx) => void;
  modalRef: LegacyRef<HTMLDialogElement> | undefined;
  onModalClose: () => void;
}

const DrugModal = ({
  drug,
  packaging,
  addPrescription,
  modalRef,
  onModalClose,
}: DrugModalProps) => {
  const [activeDose, setActiveDose] = useState<Packaging | null>(null);

  useEffect(() => {
    setActiveDose(packaging[0]);
  }, [packaging]);

  const handleSavePrescription = async (formData: FormData) => {
    const prescription = {
      ...activeDose,
      qty: formData.get('qty') as string,
      frequency: formData.get('frequency') as string,
    } as Packaging;

    const saved = await addRx(prescription);

    addPrescription({ ...saved, created_at: null });

    setActiveDose(null);
  };

  const handleDosageSelect = useCallback(
    (select: HTMLSelectElement) => {
      const dosageId = Number(select.value);

      const dosage = packaging.find((item) => item.id === dosageId);
      setActiveDose(dosage as Packaging);
    },
    [packaging]
  );

  return (
    <dialog ref={modalRef} className={`${styles.modal} ${styles.rxSearch}`}>
      {drug && packaging.length > 0 && (
        <form action={handleSavePrescription}>
          <div className="form-header">
            <h3>Drug Details</h3>
          </div>

          {/* Select Dosage dropdown */}
          <div className={styles.dropdownContainer}>
            <label htmlFor="location">Dosage</label>
            <select
              id="dosage"
              className={styles.dropdownSelect}
              name="dosage"
              onChange={(e) =>
                handleDosageSelect(e.nativeEvent.target as HTMLSelectElement)
              }
            >
              {packaging.map((dose) => {
                return (
                  <option key={dose.id} value={dose.id}>
                    {dose.name}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Amount and Refills */}
          <div className="row two-column">
            <div>
              <label htmlFor="qty" className="field-label">
                Amount per refill
              </label>
              <input
                id="qty"
                type="text"
                name="qty"
                defaultValue={activeDose?.qty}
              />
            </div>

            <div className={styles.dropdownContainer}>
              <label htmlFor="frequency" className="field-label">
                Refills last for
              </label>
              <select
                id="frequency"
                name="frequency"
                className={styles.dropdownSelect}
              >
                <option value="30">1 month</option>
                <option value="60">2 months</option>
                <option value="90">3 months</option>
                <option value="180">6 months</option>
                <option value="360">12 months</option>
              </select>
            </div>
          </div>

          <div className={styles.modalButtonContainer}>
            <button
              className={`button ${styles.cancelBtn}`}
              type="button"
              onClick={onModalClose}
            >
              Cancel
            </button>

            <button className={`button primary ${styles.saveBtn}`}>
              <span>Save Prescription</span>
              <AddCircleIcon color="white" />
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
};

export default DrugModal;
